import React, { useState, useEffect } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import axios from "axios";
import toastr from "toastr";
import FlexDatesCard from "./FlexDatesCard";
import { CustomDropdown } from "./CustomDropdown";
import InfoBox from "./InfoBox";
import CustomToolTip from "./CustomToolTip";

interface NewCampaignModalProps {
    showNewCampaignModal: boolean;
    closeModal: () => void;
    refreshCampaigns: () => void;
    policy_id: string;
    benefitEndDate: string | null;
    accrualFrequency: string | null;
    IsPlanAdmin: boolean;
    setParentLoading: (val: boolean) => void;
    onFailedStart: () => void;
}

const NewCampaignModal: React.FC<NewCampaignModalProps> = ({
    showNewCampaignModal,
    closeModal,
    refreshCampaigns,
    policy_id,
    benefitEndDate,
    accrualFrequency,
    IsPlanAdmin,
    setParentLoading,
    onFailedStart
}) => {
    const [SelectionDeadline, setSelectionDeadline] = useState<Date | null>(null);
    const [LaunchDate, setLaunchDate] = useState<Date | null>(null);
    const [ReminderDate, setReminderDate] = useState<Date | null>(null);
    const [LastDayReminder, setLastDayReminder] = useState<Date | null>(null);
    const [campaignFrequency, setCampaignFrequency] = useState<string | null>(null);

    useEffect(() => {
        setCampaignFrequency(accrualFrequency || "None");
    }, [accrualFrequency]);

    useEffect(() => {
        if (benefitEndDate) {
            const benefitEndDateObj = new Date(benefitEndDate);

            const defaultLaunchDate = new Date(benefitEndDateObj);
            defaultLaunchDate.setDate(defaultLaunchDate.getDate() - 13);

            const defaultReminderDate = new Date(defaultLaunchDate);
            defaultReminderDate.setDate(defaultReminderDate.getDate() + 5);

            const defaultLastDayReminder = new Date(benefitEndDateObj);
            defaultLastDayReminder.setDate(defaultLastDayReminder.getDate() - 1);

            setLaunchDate(defaultLaunchDate);
            setReminderDate(defaultReminderDate);
            setLastDayReminder(defaultLastDayReminder);
            setSelectionDeadline(benefitEndDateObj);
        }
    }, [benefitEndDate]);

    const handleStartCampaign = async () => {
        if (!benefitEndDate) return;

        setParentLoading(true);
        closeModal();

        const selectionDeadline = new Date(SelectionDeadline!);
        const launchDate = new Date(LaunchDate!);
        const reminderDate = new Date(ReminderDate!);
        const lastDayReminder = new Date(LastDayReminder!);
        const benefitEndDateObj = new Date(benefitEndDate);
        const today = new Date();

        if (launchDate <= today) {
            toastr.error("Launch date must be after today", "Error");
            return;
        }

        if (
            selectionDeadline > benefitEndDateObj ||
            launchDate > benefitEndDateObj ||
            reminderDate > benefitEndDateObj ||
            lastDayReminder > benefitEndDateObj
        ) {
            toastr.error("Dates cannot be after the benefit year end date", "Error");
            return;
        }

        if (
            launchDate >= selectionDeadline ||
            reminderDate >= selectionDeadline ||
            lastDayReminder >= selectionDeadline
        ) {
            toastr.error("Dates must be before the selection deadline", "Error");
            return;
        }

        if (launchDate >= reminderDate) {
            toastr.error("Launch date must be before the reminder date", "Error");
            return;
        }

        if (reminderDate >= lastDayReminder) {
            toastr.error("Reminder date must be before the last day reminder", "Error");
            return;
        }

        const body = {
            PolicyId: policy_id,
            SelectionDeadline: SelectionDeadline?.toLocaleDateString(),
            LaunchDate: LaunchDate?.toLocaleDateString(),
            ReminderDate: ReminderDate?.toLocaleDateString(),
            LastDayReminder: LastDayReminder?.toLocaleDateString(),
            CampaignFrequency: campaignFrequency === "None" ? null : campaignFrequency,
        };

        try {
            await axios.post("/api/Flex/addpolicycampaign", body);
            refreshCampaigns();
            toastr.success("Campaign started successfully");
        } catch (err) {
            toastr.error("Failed to start campaign");
            onFailedStart();
        } finally {
            setParentLoading(false);
        }
    };

    const handleDateChange = (date: Date, type: string) => {
        switch (type) {
            case "SelectionDeadline":
                setSelectionDeadline(date);
                break;
            case "LaunchDate":
                setLaunchDate(date);
                break;
            case "ReminderDate":
                setReminderDate(date);
                break;
            case "LastDayReminder":
                setLastDayReminder(date);
                break;
            default:
                break;
        }
    };

    const isAnyDateNull = !SelectionDeadline || !LaunchDate || !ReminderDate || !LastDayReminder;

    return (
        <Modal
            className="react-modal edit-campaign-modal"
            show={showNewCampaignModal}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header>
                <Modal.Title className="modal-title">New Flex Campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <InfoBox>
                    When choosing the employee email Launch date, ensure you leave yourself enough time to review the employee list that clicking ‘Start Campaign’ will generate!
                    <br />
                    Don’t worry, these dates can be adjusted at any point until they have passed.
                </InfoBox>
                <br />
                <FlexDatesCard
                    handleDateChange={handleDateChange}
                    LaunchDate={LaunchDate}
                    ReminderDate={ReminderDate}
                    LastDayReminder={LastDayReminder}
                    SelectionDeadline={SelectionDeadline}
                />

                {(accrualFrequency && !IsPlanAdmin)  && (
                        <div className="RT-TextBox" style={{ backgroundColor: "white", padding: "15px 20px", marginTop: "20px" }}>
                            <label className="RT-Label" style={{paddingLeft: 10}}>
                                Scheduled Credits Frequency
                                <CustomToolTip
                                    header="Scheduled Credits Frequency"
                                    bodyContent="Confirm the Frequency for next year's Scheduled Credits. Only Ops users may make this adjustment."
                                />
                            </label>
                            <CustomDropdown
                                options={["None", "Monthly", "Quarterly", "Semiannually"]}
                                selectedOption={campaignFrequency}
                                setSelectedOption={setCampaignFrequency}
                            />
                        </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleStartCampaign} disabled={isAnyDateNull}>
                    Start Campaign
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default NewCampaignModal;
